import { Link, useSearchParams } from 'react-router';
import { useEffect, useMemo, useState } from 'react';
import { useProducts } from '@/hooks/use-catalog';
import { useSeo } from '@/hooks/use-seo';
import { ProductCard } from '@/components/cro/product-card';
import { TrustBar } from '@/components/cro/trust-bar';
import { NetworkIcon } from '@/components/network-icon';
import { api } from '@/lib/api-client';
import { itemListJsonLd } from '@/lib/seo';
import type { ProductPublic } from '@dzseguidores/types';

type Network = { id: string; name: string; slug: string; icon: string | null };

const CATALOG_SEO: Record<
  string,
  { title: string; description: string; h1: string; intro: string }
> = {
  '': {
    title: 'Catálogo de Seguidores e Curtidas Instagram e TikTok',
    description:
      'Catálogo completo para comprar seguidores e likes no Instagram e TikTok. Compare pacotes, preços e compre com PIX na DZ Seguidores.',
    h1: 'Catálogo de seguidores e curtidas',
    intro: 'Compare pacotes, veja o mais vendido e finalize com PIX em minutos.',
  },
  instagram: {
    title: 'Catálogo Instagram — Seguidores e Curtidas',
    description:
      'Veja pacotes para comprar seguidores e curtidas Instagram. Entrega rápida, PIX e garantia na DZ Seguidores.',
    h1: 'Catálogo Instagram: seguidores e curtidas',
    intro: 'Escolha o pacote ideal de seguidores ou likes para o seu Instagram.',
  },
  tiktok: {
    title: 'Catálogo TikTok — Seguidores e Curtidas',
    description:
      'Veja pacotes para comprar seguidores e curtidas TikTok. Entrega rápida, PIX e garantia na DZ Seguidores.',
    h1: 'Catálogo TikTok: seguidores e curtidas',
    intro: 'Escolha o pacote ideal de seguidores ou likes para o seu TikTok.',
  },
};

export function CatalogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const networkFilter = searchParams.get('network') ?? '';
  const serviceFilter = searchParams.get('service') ?? '';
  const { data, isLoading, error } = useProducts(networkFilter || undefined);
  const allProducts: ProductPublic[] = data?.items ?? [];
  const [networks, setNetworks] = useState<Network[]>([]);
  const seo = CATALOG_SEO[networkFilter] ?? {
    title: `Catálogo ${networkFilter}`,
    description: `Compre seguidores e curtidas para ${networkFilter} na DZ Seguidores.`,
    h1: `Catálogo ${networkFilter}`,
    intro: 'Escolha o serviço ideal para sua rede.',
  };

  const products = useMemo(() => {
    let list = allProducts;
    if (serviceFilter === 'followers' || serviceFilter === 'likes') {
      list = list.filter((p) => p.serviceTypeSlug === serviceFilter);
    }
    return list;
  }, [allProducts, serviceFilter]);

  useSeo({
    title: seo.title,
    description: seo.description,
    path: networkFilter ? `/catalogo?network=${networkFilter}` : '/catalogo',
    keywords:
      networkFilter === 'instagram' || networkFilter === 'tiktok'
        ? [
            `comprar seguidores ${networkFilter}`,
            `comprar curtidas ${networkFilter}`,
            `likes ${networkFilter}`,
          ]
        : undefined,
    jsonLd:
      products.length > 0
        ? itemListJsonLd(
            products.slice(0, 24).map((p, i) => ({
              name: p.name,
              slug: p.slug,
              position: i + 1,
            })),
            seo.h1,
          )
        : undefined,
  });

  useEffect(() => {
    api
      .get<Network[]>('/catalog/networks?withProducts=true')
      .then(setNetworks)
      .catch(() => setNetworks([]));
  }, []);

  useEffect(() => {
    if (!networkFilter || networks.length === 0) return;
    if (networks.some((n) => n.slug === networkFilter)) return;
    const next = new URLSearchParams(searchParams);
    next.delete('network');
    setSearchParams(next, { replace: true });
  }, [networkFilter, networks, searchParams, setSearchParams]);

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(searchParams);
    if (!value) next.delete(key);
    else next.set(key, value);
    setSearchParams(next);
  }

  return (
    <div className="container-page py-10">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold text-fg">{seo.h1}</h1>
        <p className="mt-2 text-muted">{seo.intro}</p>
        <div className="mt-4 flex flex-wrap gap-3 text-sm">
          <Link to="/instagram/seguidores" className="text-muted underline hover:text-fg">
            Seguidores Instagram
          </Link>
          <Link to="/instagram/curtidas" className="text-muted underline hover:text-fg">
            Curtidas Instagram
          </Link>
          <Link to="/tiktok/seguidores" className="text-muted underline hover:text-fg">
            Seguidores TikTok
          </Link>
          <Link to="/tiktok/curtidas" className="text-muted underline hover:text-fg">
            Curtidas TikTok
          </Link>
        </div>
      </div>

      <div className="mb-4 overflow-x-auto pb-1">
        <div className="flex min-w-max gap-2">
          <button
            type="button"
            onClick={() => setParam('network', '')}
            className={`rounded-lg border px-4 py-2 text-sm font-medium transition ${
              !networkFilter
                ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-[var(--color-accent-fg)]'
                : 'border-[var(--color-border)] bg-[var(--color-surface)] text-fg hover:bg-[var(--color-bg)]'
            }`}
          >
            Todas as redes
          </button>
          {networks.map((n) => {
            const active = networkFilter === n.slug;
            return (
              <button
                key={n.id}
                type="button"
                onClick={() => setParam('network', n.slug)}
                className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                  active
                    ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-[var(--color-accent-fg)]'
                    : 'border-[var(--color-border)] bg-[var(--color-surface)] text-fg hover:bg-[var(--color-bg)]'
                }`}
              >
                <NetworkIcon slug={n.slug} name={n.name} size={16} />
                {n.name}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mb-8 flex flex-wrap gap-2">
        {[
          { value: '', label: 'Todos os serviços' },
          { value: 'followers', label: 'Seguidores' },
          { value: 'likes', label: 'Curtidas' },
        ].map((opt) => (
          <button
            key={opt.value || 'all'}
            type="button"
            onClick={() => setParam('service', opt.value)}
            className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition ${
              serviceFilter === opt.value
                ? 'border-fg bg-fg text-[var(--color-bg)]'
                : 'border-border bg-surface text-muted hover:text-fg'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {isLoading && <p className="text-muted">Carregando produtos…</p>}
      {error && (
        <p className="glass rounded-xl p-4 text-sm text-danger">
          Não foi possível carregar o catálogo. Verifique se a API está online.
        </p>
      )}

      {!isLoading && !error && products.length === 0 && (
        <p className="glass rounded-xl p-4 text-sm text-muted">
          Nenhum produto encontrado para este filtro.
        </p>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((product, i) => (
          <ProductCard key={product.id} product={product} peers={products} index={i} />
        ))}
      </div>

      <div className="mt-14 border-t border-border pt-10">
        <h2 className="text-lg font-semibold">Compra segura</h2>
        <TrustBar className="mt-6" />
      </div>
    </div>
  );
}
